// One saved item as the mobile Library shows it: a picture (or a few lines
// of text), a title, where it came from, and the haystack search runs over.
//
// Every Library surface (the Everything grid, search results, Spaces and
// the detail view) renders LibraryCard, never raw items, so the rules for
// "which image, which title, how tall" live in one place. Pure.

import type { Item } from '@/stores/canvasStore';
import { decodeHtmlEntities } from '@/lib/htmlEntities';
import { carouselSlides, isUsableSocialImage, needsCreativeRehost, needsPreviewRefresh, socialCaption, socialMediaType, type CreativeContent, type SocialCardContent } from '@/lib/socialCard';
import { classifySource, sourceMeta, type LibrarySource } from './sources';
import { postedAtFromUrl } from './postDate';
import { noteBodyToText } from './noteBody';

/** How a tile draws: a picture (title line under it), a text tile, a tweet card, or a PDF's first page. */
export type TileKind = 'media' | 'text' | 'tweet' | 'pdf' | 'site';

/** One picture of a post: a carousel slide, a tweet's photo or video cover. */
export interface CardMedia {
  src: string;
  video: boolean;
}

export interface CardAuthor {
  name: string;
  handle: string;
  avatar: string | null;
}

export interface CardStats {
  replies: number;
  reposts: number;
  likes: number;
  views: number;
}

export interface LibraryCard {
  id: string;
  item: Item;
  source: LibrarySource;
  /** One line under the tile. May be empty for bare images. */
  title: string;
  /** Author handle, domain or filename: the detail view's second line. */
  subtitle: string;
  /** Image to show, or null for a text tile. */
  thumbnail: string | null;
  /** Width / height of the thumbnail, clamped to 9:16 ... 1.91:1. Text tiles use 4:5. */
  aspectRatio: number;
  isVideo: boolean;
  isCarousel: boolean;
  /** Body text for text tiles (notes, text-only posts), plain, at most ~600 chars. */
  text: string;
  /** The original link, when there is one. */
  url: string | null;
  boardId: string | null;
  /** When it was saved here (an import's rows all share the moment of the import). */
  createdAt: string;
  /**
   * Where it sits in newest-first order, in ms: the save time, or for a post
   * that came in a bulk import, when it was published (see toLibraryCards).
   */
  feedTime: number;
  /** AI subject labels ("Interior", "Cars"). Empty until tagging has run. */
  subjects: string[];
  /** Lowercased text search matches against. */
  searchText: string;
  tile: TileKind;
  /** Whether the grid draws the title line under the tile (pictures and PDFs). */
  showsTitle: boolean;
  /** Width / height of the media itself, for the detail view (a tweet tile's ratio includes its text). */
  mediaRatio: number;
  /** Every picture, first = thumbnail: carousels and multi-photo tweets swipe through these. */
  media: CardMedia[];
  author: CardAuthor | null;
  /** The whole caption or post text, untruncated, for the detail view. Empty when there is none. */
  caption: string;
  stats: CardStats | null;
  /** A file's own address (PDFs render their first page from it). */
  fileUrl: string | null;
  /** A website's icon, for the site tile drawn when the page gave no picture. */
  favicon?: string | null;
}

const MIN_RATIO = 9 / 16;
const MAX_RATIO = 1.91;
const TEXT_RATIO = 4 / 5;
/** A website with no picture: its icon, name and address on raised paper, a little wider than tall. */
const SITE_RATIO = 1.1;
/** A PDF tile: the desktop card's page stack fits a portrait page or a slide deck at 4:5. */
const PDF_TILE_RATIO = 4 / 5;
/** fetch-link-metadata's screenshots: 1280 x 800. */
const SCREENSHOT_RATIO = 1.6;

export function clampRatio(ratio: number | null | undefined, fallback: number): number {
  const r = typeof ratio === 'number' && Number.isFinite(ratio) && ratio > 0 ? ratio : fallback;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, r));
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function decode(s: string): string {
  return (decodeHtmlEntities(s) ?? s).trim();
}

function firstLine(s: string): string {
  return s.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function youTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1) || null;
    if (u.hostname.includes('youtube.com')) {
      if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null;
      return u.searchParams.get('v');
    }
  } catch {
    // not a URL
  }
  return null;
}

/** Item's own canvas size as a ratio: uploads store the image's proportions there. */
function itemRatio(item: Item): number | undefined {
  return item.width > 0 && item.height > 0 ? item.width / item.height : undefined;
}

function subjectsOf(item: Item): string[] {
  const raw = (item as Item & { subjects?: unknown }).subjects;
  return Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string' && s.length > 0) : [];
}

interface Visual {
  title: string;
  subtitle: string;
  thumbnail: string | null;
  aspectRatio: number;
  isVideo: boolean;
  isCarousel: boolean;
  text: string;
  url: string | null;
  extraSearch: string[];
  tile?: TileKind;
  mediaRatio?: number;
  media?: CardMedia[];
  author?: CardAuthor | null;
  caption?: string;
  stats?: CardStats | null;
  fileUrl?: string | null;
  favicon?: string | null;
}

/** A post's own photo or video address (x.com/u/status/1/photo/1): it points at the media shown with it. */
const TWEET_MEDIA_LINK = /[ \t]*https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/[^\s/]+\/status\/\d+\/(?:photo|video)\/\d+\S*/gi;
/** A t.co short link closing a post with media: X appends it for the media. */
const TRAILING_TCO = /\s*https?:\/\/t\.co\/\w+\s*$/i;

/**
 * A tweet's text as it reads on X: without the addresses of its own photos
 * and videos (shown with it already), which otherwise run on as long
 * unbreakable lines.
 */
export function tweetDisplayText(text: string, hasMedia: boolean): string {
  let out = text.replace(TWEET_MEDIA_LINK, '');
  if (hasMedia) out = out.replace(TRAILING_TCO, '');
  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Tweet tiles hold their text, so their height follows its length. Measured at a 2-column tile (~171px). */
const TWEET_TILE_WIDTH = 171;
export function estimateTweetRatio(text: string, mediaRatio: number | null): number {
  const w = TWEET_TILE_WIDTH;
  // Measured on the 2-column tile: about 20 characters a line at 13px, a 40px author row
  const perLine = 20;
  let lines = 0;
  for (const para of text.split('\n')) lines += Math.max(1, Math.ceil(para.trim().length / perLine));
  lines = Math.min(text.trim() ? lines : 0, mediaRatio ? 6 : 10);
  const media = mediaRatio ? Math.min((w - 24) / mediaRatio, w * 1.1) + 10 : 0;
  const height = 12 + 40 + 8 + lines * 19 + media + 12;
  return Math.min(1.6, Math.max(0.3, w / height));
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function visualFor(item: Item, source: LibrarySource): Visual {
  const c = (item.content ?? {}) as Record<string, unknown>;
  const base: Visual = {
    title: '',
    subtitle: '',
    thumbnail: null,
    aspectRatio: TEXT_RATIO,
    isVideo: false,
    isCarousel: false,
    text: '',
    url: null,
    extraSearch: [str(c.summary), str(c.analysis), str(c.aiDescription), str(c.alt), str(c.searchText), str(c.transcript)],
  };

  switch (item.item_type) {
    case 'sticky': {
      const text = str(c.text).trim();
      return { ...base, title: firstLine(text), text };
    }
    case 'text_card':
    case 'document': {
      // Phone notes are plain text, desktop ones HTML; either reads the same on the tile
      const body = noteBodyToText(str(c.body) || str(c.content));
      const title = decode(str(c.title)) || firstLine(body);
      return { ...base, title, text: body.slice(0, 600) };
    }
    case 'image': {
      const caption = str(c.summary) || str(c.alt);
      return {
        ...base,
        title: caption && caption !== 'Image' ? firstLine(caption) : '',
        subtitle: str(c.filename),
        thumbnail: str(c.url) || null,
        aspectRatio: clampRatio(itemRatio(item), 1),
      };
    }
    case 'video':
      return {
        ...base,
        title: str(c.filename) || str(c.summary) || 'Video',
        subtitle: str(c.filename),
        thumbnail: str(c.thumbnailUrl) || null,
        aspectRatio: clampRatio(itemRatio(item), 16 / 9),
        isVideo: true,
      };
    case 'file': {
      const filename = str(c.filename) || 'File';
      return {
        ...base,
        title: filename,
        subtitle: filename,
        thumbnail: str(c.thumbnailUrl) || null,
        // Room for the page stack: the page in front, the next one peeking out, dots under them
        aspectRatio: source === 'pdf' ? PDF_TILE_RATIO : TEXT_RATIO,
        text: str(c.extracted_text).slice(0, 600),
        tile: source === 'pdf' && str(c.url) ? 'pdf' : undefined,
        fileUrl: str(c.url) || null,
      };
    }
    case 'scraped_creative': {
      const thumb = str(c.thumbnail_url);
      const handle = str(c.account_handle);
      return {
        ...base,
        title: firstLine(decode(str(c.hook_text) || str(c.caption))) || (handle ? `@${handle}` : ''),
        subtitle: handle ? `@${handle}` : '',
        thumbnail: thumb || null,
        aspectRatio: clampRatio(c.media_aspect_ratio as number, 4 / 5),
        isVideo: !!c.video_url || /video|reel/i.test(str(c.content_type)),
        url: str(c.content_url) || str(c.source_url) || null,
        extraSearch: [...base.extraSearch, str(c.caption), str(c.account_name), handle],
        author: handle || str(c.account_name) ? { name: str(c.account_name), handle, avatar: null } : null,
        caption: decode(str(c.caption) || str(c.hook_text)),
      };
    }
    case 'link': {
      // X post from bookmark sync or a pasted tweet
      if (c.tweet_id) {
        const author = (c.author ?? {}) as Record<string, unknown>;
        const media = Array.isArray(c.media) ? (c.media as Array<Record<string, unknown>>) : [];
        const first = media[0];
        const thumb = first ? str(first.preview_url) || (str(first.type) === 'photo' ? str(first.url) : '') : '';
        const text = tweetDisplayText(decode(str(c.text)), media.length > 0);
        const w = first ? Number(first.width) : 0;
        const h = first ? Number(first.height) : 0;
        const mediaRatio = thumb ? clampRatio(w > 0 && h > 0 ? w / h : undefined, 16 / 9) : TEXT_RATIO;
        const allMedia: CardMedia[] = media
          .map((m) => ({ src: str(m.preview_url) || (str(m.type) === 'photo' ? str(m.url) : ''), video: /video|gif/.test(str(m.type)) }))
          .filter((m) => m.src);
        const metrics = (c.metrics ?? {}) as Record<string, unknown>;
        return {
          ...base,
          title: firstLine(text),
          subtitle: str(author.username) ? `@${str(author.username)}` : '',
          thumbnail: thumb || null,
          aspectRatio: estimateTweetRatio(text, thumb ? mediaRatio : null),
          isVideo: !!first && /video|gif/.test(str(first.type)),
          text,
          url: str(c.tweet_url) || null,
          extraSearch: [...base.extraSearch, str(author.name), str(author.username)],
          tile: 'tweet',
          mediaRatio,
          media: allMedia,
          author: { name: str(author.name), handle: str(author.username), avatar: str(author.profile_image_url) || null },
          caption: text,
          stats: { replies: num(metrics.replies), reposts: num(metrics.retweets), likes: num(metrics.likes), views: num(metrics.views) },
        };
      }

      const url = str(c.url);
      const social = c as SocialCardContent;
      const title = decode(str(c.title));
      const description = decode(str(c.description));
      const handle = str(c.authorHandle);
      const subtitle = handle ? `@${handle}` : str(c.siteName) || hostOf(url);
      const extraSearch = [...base.extraSearch, description, str(c.siteName), str(c.authorName), handle, url];

      if (source === 'instagram_reel' || source === 'instagram_post' || source === 'tiktok') {
        const type = socialMediaType(social);
        const image = isUsableSocialImage(str(c.image)) ? str(c.image) : null;
        const slides = carouselSlides(social).map((sl) => ({ src: sl.image, video: !!sl.video }));
        return {
          ...base,
          title: socialCaption(social),
          subtitle,
          thumbnail: image,
          aspectRatio: clampRatio(image ? (c.imageAspectRatio as number) : undefined, type === 'video' ? 9 / 16 : 4 / 5),
          isVideo: type === 'video',
          isCarousel: type === 'carousel',
          url,
          extraSearch,
          media: slides.length > 1 ? slides : image ? [{ src: image, video: type === 'video' }] : [],
          author: handle || str(c.authorName) ? { name: str(c.authorName), handle, avatar: str(c.authorAvatar) || null } : null,
          caption: description || '',
        };
      }

      const ytId = source === 'youtube' ? youTubeId(url) : null;
      const ogImage = str(c.image);
      const shot = str(c.screenshot);
      const image = ogImage || shot || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');
      const isVideo = source === 'youtube' || source === 'vimeo' || str(c.contentType) === 'video';
      // The page gave no picture (and no screenshot yet): a site tile, never a blank text box
      const site = !image && !description.trim();
      return {
        ...base,
        title: title || hostOf(url),
        subtitle,
        thumbnail: image || null,
        aspectRatio: image ? clampRatio(c.imageAspectRatio as number, isVideo ? 16 / 9 : !ogImage && shot ? SCREENSHOT_RATIO : 1.91) : site ? SITE_RATIO : TEXT_RATIO,
        isVideo,
        text: image ? '' : description.slice(0, 600),
        url,
        extraSearch,
        ...(site ? { tile: 'site' as const, favicon: str(c.favicon) || null } : {}),
      };
    }
    default:
      return base;
  }
}

/** The card for one item, or null when the Library does not show it. */
export function toLibraryCard(item: Item): LibraryCard | null {
  const source = classifySource(item);
  if (!source) return null;
  const v = visualFor(item, source);
  const subjects = subjectsOf(item);
  const searchText = [v.title, v.subtitle, v.text, sourceMeta(source).label, ...subjects, ...v.extraSearch]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return {
    id: item.id,
    item,
    source,
    title: v.title,
    subtitle: v.subtitle,
    thumbnail: v.thumbnail,
    aspectRatio: v.thumbnail || v.tile === 'tweet' || v.tile === 'pdf' ? v.aspectRatio : TEXT_RATIO,
    isVideo: v.isVideo,
    isCarousel: v.isCarousel,
    text: v.text,
    url: v.url,
    boardId: item.board_id ?? null,
    createdAt: item.created_at,
    feedTime: Date.parse(item.created_at) || 0,
    subjects,
    searchText,
    ...finish(v),
  };
}

/** The fields every card gets from its visual, with defaults for the plain kinds. */
function finish(v: Visual): Pick<LibraryCard, 'tile' | 'showsTitle' | 'mediaRatio' | 'media' | 'author' | 'caption' | 'stats' | 'fileUrl' | 'favicon'> {
  const tile: TileKind = v.tile ?? (v.thumbnail ? 'media' : 'text');
  return {
    tile,
    showsTitle: !!v.title && (tile === 'pdf' || (tile === 'media' && !!v.thumbnail)),
    mediaRatio: v.mediaRatio ?? (v.thumbnail ? v.aspectRatio : TEXT_RATIO),
    media: v.media ?? (v.thumbnail ? [{ src: v.thumbnail, video: v.isVideo }] : []),
    author: v.author ?? null,
    caption: v.caption ?? '',
    stats: v.stats ?? null,
    fileUrl: v.fileUrl ?? null,
    favicon: v.favicon ?? null,
  };
}

/**
 * Newest first, and always the same order: cards saved in the same instant
 * fall back to their id, so a refetch (after a delete, say) never reshuffles them.
 */
export function byNewest(a: LibraryCard, b: LibraryCard): number {
  return b.feedTime - a.feedTime || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0);
}

/**
 * This many items saved in the same instant are a bulk import, not saves:
 * people save one thing at a time, an import inserts hundreds per statement.
 */
export const BULK_IMPORT_MIN = 20;

/**
 * Every showable item as a card, newest first. Ghost (streaming) items are skipped.
 *
 * A bulk import has no save dates, so left alone it would bury everything
 * else under one block of "just now". Its social posts are placed at when
 * they were published instead (read off the link), which is the closest we
 * know to when they were saved; anything without a readable date keeps the
 * import time.
 */
export function toLibraryCards(items: Item[]): LibraryCard[] {
  const perInstant = new Map<string, number>();
  for (const item of items) perInstant.set(item.created_at, (perInstant.get(item.created_at) ?? 0) + 1);
  const now = Date.now();
  const cards: LibraryCard[] = [];
  for (const item of items) {
    if (item.isGhost) continue;
    const card = toLibraryCard(item);
    if (!card) continue;
    if ((perInstant.get(item.created_at) ?? 0) >= BULK_IMPORT_MIN) {
      const posted = postedAtFromUrl(card.url, now);
      if (posted !== null && posted < card.feedTime) card.feedTime = posted;
    }
    cards.push(card);
  }
  return cards.sort(byNewest);
}

/**
 * Whether a card's preview should be fetched again, and how: the same rules
 * the desktop cards follow. Instagram and TikTok links saved without a
 * picture get a free fetch, then a deep one if that came back empty;
 * pulled creatives with expired thumbnails get re-hosted.
 */
export function previewRefreshFor(item: Item): 'free' | 'deep' | 'creative' | null {
  const c = (item.content ?? {}) as Record<string, unknown>;
  if (item.item_type === 'link' && !c.tweet_id) return needsPreviewRefresh(c as SocialCardContent) ?? websiteRefresh(c);
  if (item.item_type === 'scraped_creative') return needsCreativeRehost(c as CreativeContent) ? 'creative' : null;
  return null;
}

/**
 * A website saved with neither a picture nor a screenshot gets one more free
 * fetch, which takes a screenshot (the upload that stores it was failing).
 * The refresh stamps previewUpdatedAt, so a page that still has nothing is
 * not asked again.
 */
function websiteRefresh(c: Record<string, unknown>): 'free' | null {
  const url = typeof c.url === 'string' ? c.url : '';
  if (!/^https?:\/\//i.test(url) || c.platform || c.previewUpdatedAt) return null;
  if (/(^|\.)(youtube\.com|youtu\.be|x\.com|twitter\.com)\//i.test(url.replace(/^https?:\/\/(www\.)?/i, ''))) return null;
  const has = (v: unknown) => typeof v === 'string' && v.length > 0;
  return has(c.image) || has(c.screenshot) ? null : 'free';
}
